import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch, callApi, detailMessage } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import Card from '../components/Card';
import InfoTip from '../components/InfoTip';
import StatTile from '../components/StatTile';
import {
  Badge, EmptyState, Field, Icon, PageHeader, Pagination, TableSkeleton, Tabs, cx,
} from '../components/ui';
import {
  TimeChart, Histogram, PhaseRibbon, formatCost, formatMs, formatNumber, phasesOn, PALETTE,
} from '../components/charts';
import PercentileChart, { latencyHasSamples } from '../components/dashboard/PercentileChart';
import LlmRequestsSection from '../components/dashboard/LlmRequestsSection';
import ToolsSection, { TurnLatencyCard } from '../components/dashboard/ToolsSection';
import DatabaseCharts from '../components/platform/DatabaseCharts';
import { HealthBadge } from '../components/platform/OverviewPanel';
import ServiceDetail from '../components/platform/ServiceDetail';
import SourceWarnings from '../components/platform/SourceWarnings';
import {
  HEALTH_LABEL, KIND_GROUP_LABEL, KIND_LABEL, THRESHOLDS, peakValue,
} from '../observability/contract';
import usePolling from '../utils/usePolling';

/* ── Load tests (Operations, superadmins) ─────────────────────────────────────
   Loads the platform with test users and reports how it held up. A run has up to three
   phases, and the form says where it stops:
     1 Onboarding  the users are created and provisioned, until each agent answers;
     2 Messages    they chat through their channels' real bridges;
     3 Tools       they ask for tools, one-shot reminders and workflows.
   The work is the control-plane's (services/load_tests.py, backend/loadtest/); this page
   starts runs and reads their reports. The report keeps everything it shows, CloudWatch
   series included, until the run is deleted.

   One run at a time per environment, and only where LOAD_TESTS_ENABLED is set:
   elsewhere the sidebar shows the entry grayed out and this page says why. A run
   page is this page with `?run=<id>`, so no route is added. */

const ACTIVE = ['queued', 'preparing', 'ramping', 'running', 'cleaning'];

const PAGE_TABS = [
  { value: 'new', label: 'New run', icon: 'play' },
  { value: 'runs', label: 'Runs', icon: 'history' },
];

const STATUS_TONE = {
  queued: 'info', preparing: 'info', ramping: 'info', running: 'info', cleaning: 'warn',
  completed: 'ok', kept: 'violet', failed: 'danger', interrupted: 'warn',
};

const REASON_LABEL = {
  finished: 'Finished', stopped: 'Stopped', ready_timeout: 'Some agents never came up',
  reply_timeout: 'Some messages were never answered',
  spend_cap: 'Spend cap reached', error: 'Error', worker_lost: 'Worker lost',
};

const PHASES = [
  { n: 1, label: 'Onboarding', help: 'Create and provision the users, until every agent answers.' },
  { n: 2, label: 'Messages', help: 'Then each user chats through its channel.' },
  { n: 3, label: 'Tools', help: 'Then each user asks for tools, reminders and workflows. Needs a real tier.' },
];

const CHANNEL_LABEL = { telegram: 'Telegram', website: 'Website', rossapp: 'Ross app' };

// One colour per stage wherever a chart of the report shows it, so the users', pods' and
// nodes' charts read alike: the offered load grey, waiting amber, answering green, failing
// red. (Pod pending was red and Failed green before.)
const STAGE_COLOR = {
  requested: PALETTE[9], created: PALETTE[3], scheduled: PALETTE[7], running: PALETTE[2],
  ready: PALETTE[1], answering: PALETTE[5], waiting: PALETTE[6], failed: PALETTE[4],
  removed: PALETTE[8], idle: PALETTE[0],
};

const STATE_SERIES = [
  { key: 'users_waiting', label: 'Waiting to start', color: STAGE_COLOR.requested },
  { key: 'users_provisioning', label: 'Provisioning', color: STAGE_COLOR.created },
  { key: 'users_pod_pending', label: 'Pod pending', color: STAGE_COLOR.waiting },
  { key: 'users_pod_starting', label: 'Pod starting', color: STAGE_COLOR.scheduled },
  { key: 'users_answering', label: 'Answering', color: STAGE_COLOR.answering },
  { key: 'users_hibernated', label: 'Hibernated', color: STAGE_COLOR.idle },
  { key: 'users_failed', label: 'Failed', color: STAGE_COLOR.failed },
  { key: 'users_deleted', label: 'Deleted', color: STAGE_COLOR.removed },
];

const DEFAULT_FORM = {
  name: '', stop_after: 1, users: 5, deploy_window_s: 60, ramp: 'linear', hold_s: 60,
  ready_timeout_s: 900, max_in_flight: 100, llm: 'mock', mock_ttft_ms: 800,
  mock_tokens_per_s: 40, tier_id: '', channels: [], msgs_per_user: 3, msgs_window_s: 120,
  drain_timeout_s: 300, tools: [], tool_msgs_per_user: 3, tools_window_s: 180,
  crons_per_user: 1, workflows_per_user: 0, keep: false, spend_cap_eur: '',
};

const NUMBER_FIELDS = ['stop_after', 'users', 'deploy_window_s', 'hold_s', 'ready_timeout_s',
  'max_in_flight', 'mock_ttft_ms', 'mock_tokens_per_s', 'msgs_per_user', 'msgs_window_s',
  'drain_timeout_s', 'tool_msgs_per_user', 'tools_window_s', 'crons_per_user',
  'workflows_per_user'];

// A run's model. One from before the mock model has no `llm`: its users were created on
// its tier, whose model answered.
const runLlm = p => p.llm || (p.tier_id != null ? 'real' : 'mock');

/* A run's settings as the form holds them, to run it again after a change and compare the
   two (Mark, 2026-10-18): what the run stored, over the form's defaults for what it did not
   store (a phase it did not reach, a setting newer than the run). A mocked run's tier was
   the default one, which the form picks again. */
function formFromRun(run) {
  const p = run?.params || {};
  const form = { ...DEFAULT_FORM, name: run?.name || '' };
  NUMBER_FIELDS.forEach((k) => { if (Number.isFinite(p[k])) form[k] = p[k]; });
  if (Object.keys(RAMP_LABEL).includes(p.ramp)) form.ramp = p.ramp;
  form.llm = runLlm(p);
  form.tier_id = form.llm === 'real' && p.tier_id != null ? String(p.tier_id) : '';
  form.channels = Array.isArray(p.channels) ? p.channels : [];
  // The run keeps each tool's name by its id.
  form.tools = Array.isArray(p.tools) ? p.tools : Object.keys(p.tools || {});
  form.keep = Boolean(p.keep);
  form.spend_cap_eur = p.spend_cap_eur != null ? String(p.spend_cap_eur) : '';
  return form;
}

/* What a run loaded again asked for that this environment no longer offers. The form
   leaves it out and says so: a run meant to be compared with another should not differ
   from it unnoticed. */
function notCarried(run, status, tiers) {
  const p = run?.params || {};
  const stopAfter = p.stop_after || 1;
  const out = [];
  if (stopAfter >= 2 && status?.channels) {
    (p.channels || []).filter(c => !status.channels.includes(c))
      .forEach(c => out.push(`the ${CHANNEL_LABEL[c] || c} channel`));
  }
  if (stopAfter >= 3 && status?.tools) {
    const offered = status.tools.map(t => t.id);
    const names = Array.isArray(p.tools) ? {} : (p.tools || {});
    (Array.isArray(p.tools) ? p.tools : Object.keys(names)).filter(t => !offered.includes(t))
      .forEach(t => out.push(`the ${names[t] || t} tool`));
  }
  if (runLlm(p) === 'real' && p.tier_id != null && tiers.length
      && !tiers.some(t => String(t.id) === String(p.tier_id))) {
    out.push(`the tier ${p.tier_name || p.tier_id}`);
  }
  return out;
}

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
// A run's date in the runs list: "18 Oct 2026, 12:26", with a month name that cannot be
// misread as D/M or M/D, as the Admins page writes dates; the full time in the tooltip.
const runDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');
const ms = (v) => (v != null ? formatMs(v) : '—');

function duration(a, b) {
  if (!a) return '—';
  const s = Math.max(0, Math.round(((b ? new Date(b) : new Date()) - new Date(a)) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

async function readJson(resp) {
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(detailMessage(body.detail, `HTTP ${resp.status}`));
  return body;
}

function StatusBadge({ status }) {
  return (
    <Badge tone={STATUS_TONE[status] || 'neutral'} dot pulse={ACTIVE.includes(status)}>
      {status}
    </Badge>
  );
}

function toggle(list, value) {
  return list.includes(value) ? list.filter(v => v !== value) : [...list, value];
}

/* ── New run ────────────────────────────────────────────────────────────── */

/* A section of the form: a phase's number on its tint, as the report's strips and phase
   heads draw it. The run's own settings have no number (a grey swatch there read as an
   unticked checkbox, right above one). */
function FormSection({ n, title }) {
  return (
    <h3 className="lt-section">
      {n ? (
        <>
          <PhaseChip span={{ kind: 'phase', n }} />
          <span className="sr-only">Phase {n}: </span>
        </>
      ) : null}
      {title}
    </h3>
  );
}

/* `from`: the run whose settings the form starts from (Runs, Run again), or null for the
   defaults. */
function NewRun({ status, from, onStarted, onClear }) {
  const [form, setForm] = useState(() => (from ? formFromRun(from) : DEFAULT_FORM));
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => {
    apiFetch('/api/tiers').then(readJson).then((list) => {
      const rows = Array.isArray(list) ? list : [];
      setTiers(rows);
      // The default tier, unless the form holds one that still exists.
      const dflt = rows.find(t => t.is_default) || rows[0];
      if (dflt) {
        setForm(f => (rows.some(t => String(t.id) === f.tier_id) ? f
          : { ...f, tier_id: String(dflt.id) }));
      }
    }).catch(() => setTiers([]));
  }, []);

  // The channels and tools this environment offers: all of them, on until unticked. A run
  // loaded again keeps those of its own still offered.
  useEffect(() => {
    if (!status?.channels) return;
    const served = status.channels;
    const offered = (status.tools || []).map(t => t.id);
    setForm((f) => {
      const channels = f.channels.filter(c => served.includes(c));
      const tools = f.tools.filter(t => offered.includes(t));
      return {
        ...f, channels: channels.length ? channels : served, tools: tools.length ? tools : offered,
      };
    });
  }, [status]);
  const left = useMemo(() => (from ? notCarried(from, status, tiers) : []), [from, status, tiers]);

  const set = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [key]: v }));
  };
  const stopAfter = Number(form.stop_after) || 1;
  const setPhase = (n) => setForm(f => ({ ...f, stop_after: n, llm: n >= 3 ? 'real' : f.llm }));

  const disabled = !status?.enabled || Boolean(status?.active_run);
  const users = Number(form.users) || 0;
  const cpu = (status?.agent_cpu_request || 0) * users;
  const mem = (status?.agent_memory_request_gb || 0) * users;
  const real = form.llm === 'real';
  const noChannels = stopAfter >= 2 && !form.channels.length;
  const toolMsgs = Number(form.tool_msgs_per_user) || 0;
  const noTools = stopAfter >= 3 && toolMsgs > 0 && !form.tools.length;

  const start = async (e) => {
    e.preventDefault();
    if (disabled || noChannels || noTools) return;
    const msgs = stopAfter >= 2 ? users * (Number(form.msgs_per_user) || 0) : 0;
    const asks = stopAfter >= 3 ? users * (toolMsgs + (Number(form.crons_per_user) || 0)
      + (Number(form.workflows_per_user) || 0)) : 0;
    const ok = await confirm({
      title: 'Start this load test?',
      body: (
        <>
          <p>
            It creates {formatNumber(users)} test users and provisions an agent for each over{' '}
            {formatNumber(Number(form.deploy_window_s) || 0)} s: about {cpu.toFixed(1)} vCPU and{' '}
            {mem.toFixed(1)} GiB requested on the agent pool. Karpenter adds nodes as it needs them,
            and they cost money while they run.
          </p>
          {stopAfter >= 2 && (
            <p>
              Then {formatNumber(msgs)} messages{stopAfter >= 3 ? ` and ${formatNumber(asks)} tool, reminder and workflow requests` : ''},{' '}
              {real ? 'answered by the tier\'s real model, which is billed' : 'answered by the mock model, billed 0'}.
              {stopAfter >= 3 ? ' Web pages, searches and images come from a built-in test site, and speech goes only to the AI Factory\'s models: besides the models, the run reaches nothing outside the platform.' : ''}
            </p>
          )}
          <p>
            {form.keep
              ? 'The users are kept after the run, until you press Clean up.'
              : 'Everything the run creates is deleted when it ends. The report stays until you delete it.'}
          </p>
        </>
      ),
      confirmLabel: 'Start the run',
      tone: 'primary',
    });
    if (!ok) return;
    setBusy(true);
    const body = { ...form };
    NUMBER_FIELDS.forEach(k => { body[k] = Number(form[k]); });
    // A mocked run's users sit on the default tier, which the backend picks.
    body.tier_id = real && form.tier_id !== '' ? Number(form.tier_id) : null;
    body.spend_cap_eur = form.spend_cap_eur === '' ? null : Number(form.spend_cap_eur);
    if (stopAfter < 2) body.channels = [];
    if (stopAfter < 3) body.tools = [];
    const run = await callApi('/api/load-tests', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }, { successMsg: 'Load test started', errorMsg: 'The load test did not start' });
    setBusy(false);
    if (run?.id) onStarted(run.id);
  };

  return (
    <form className="card" onSubmit={start} aria-labelledby="lt-new-title">
      <div className="card-header">
        <h2 className="card-title" id="lt-new-title">New run</h2>
      </div>

      {from && (
        <div className="notice notice-info" role="status">
          <Icon name="refresh" />
          <div className="notice-body">
            The settings of <strong>{from.name}</strong>, run on {runDate(from.created_at)}.
            {left.length > 0 && ` Not offered here any more, so not carried over: ${left.join(', ')}.`}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClear}>
            Use the defaults
          </button>
        </div>
      )}

      <div className="form-row cols-2">
        <Field label="Name" htmlFor="lt-name" required hint="What the run is for.">
          <input id="lt-name" className="form-input" value={form.name} onChange={set('name')}
                 maxLength={120} required />
        </Field>
        <fieldset className="lt-fieldset">
          <legend>Stop after</legend>
          <div className="lt-options" role="radiogroup" aria-label="Stop after">
            {/* Each phase with its number on the tint the report's strips draw it in. */}
            {PHASES.map(p => (
              <label key={p.n} className="checkbox-row" htmlFor={`lt-phase-${p.n}`}>
                <input id={`lt-phase-${p.n}`} type="radio" name="lt-phase" value={p.n}
                       checked={stopAfter === p.n} onChange={() => setPhase(p.n)} />
                <span className="lt-option">
                  <PhaseChip span={{ kind: 'phase', n: p.n }} />
                  <span><strong>{p.label}</strong> <span className="text-muted">{p.help}</span></span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <FormSection n={1} title="Onboarding" />
      <div className="form-row cols-3">
        <Field label="Users" htmlFor="lt-users" required hint="No upper limit.">
          <input id="lt-users" className="form-input" type="number" min={1} step={1}
                 value={form.users} onChange={set('users')} required />
        </Field>
        <Field label="Deploy window (s)" htmlFor="lt-window"
               hint="The users are created and provisioned over this time. 0 sends them all at once.">
          <input id="lt-window" className="form-input" type="number" min={0} step={1}
                 value={form.deploy_window_s} onChange={set('deploy_window_s')} />
        </Field>
        <Field label="Ramp" htmlFor="lt-ramp"
               hint="Linear: evenly spaced. Gaussian: a burst in the middle of the window. Burst: all at the start.">
          <select id="lt-ramp" className="form-input" value={form.ramp} onChange={set('ramp')}>
            <option value="linear">Linear</option>
            <option value="gaussian">Gaussian (burst in the middle)</option>
            <option value="burst">All at once</option>
          </select>
        </Field>
      </div>
      <div className="form-row cols-3">
        <Field label="Ready timeout (s)" htmlFor="lt-timeout"
               hint="How long to wait for every agent before giving up on the rest.">
          <input id="lt-timeout" className="form-input" type="number" min={60} step={1}
                 value={form.ready_timeout_s} onChange={set('ready_timeout_s')} />
        </Field>
        <Field label="Provision requests in flight" htmlFor="lt-inflight"
               hint="At most this many open at once. The report says when this limited the run.">
          <input id="lt-inflight" className="form-input" type="number" min={1} step={1}
                 value={form.max_in_flight} onChange={set('max_in_flight')} />
        </Field>
        <Field label="LLM" htmlFor="lt-llm"
               hint={stopAfter >= 3 ? 'The tools phase needs a real model to choose the tools.'
                 : 'Mock: random replies after a simulated latency, nothing billed. Real: the tier\'s own model.'}>
          <select id="lt-llm" className="form-input" value={form.llm} onChange={set('llm')}
                  disabled={stopAfter >= 3}>
            <option value="mock">Mock tier (fake replies)</option>
            <option value="real">Real tier</option>
          </select>
        </Field>
      </div>
      <div className="form-row cols-3">
        {real ? (
          <Field label="Tier" htmlFor="lt-tier" required hint="The tier the users are created on, and whose model answers.">
            <select id="lt-tier" className="form-input" value={form.tier_id} onChange={set('tier_id')} required>
              {tiers.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
            </select>
          </Field>
        ) : (
          <>
            <Field label="Mock time to first token (ms)" htmlFor="lt-ttft"
                   hint="The median; each reply draws around it.">
              <input id="lt-ttft" className="form-input" type="number" min={0} step={50}
                     value={form.mock_ttft_ms} onChange={set('mock_ttft_ms')} />
            </Field>
            <Field label="Mock output speed (tokens/s)" htmlFor="lt-tps"
                   hint={`Replies of 30 to 220 tokens. Users sit on ${status?.mock_tier?.name || 'the default tier'} for their configuration.`}>
              <input id="lt-tps" className="form-input" type="number" min={1} step={1}
                     value={form.mock_tokens_per_s} onChange={set('mock_tokens_per_s')} />
            </Field>
          </>
        )}
      </div>

      {stopAfter >= 2 && (
        <>
          <FormSection n={2} title="Messages" />
          <fieldset className="lt-fieldset">
            <legend>Channels</legend>
            <p className="text-muted">Each user talks through one, in turn. Only the channels this environment serves.</p>
            <div className="lt-options">
              {(status?.channels || []).map(c => (
                <label key={c} className="checkbox-row" htmlFor={`lt-ch-${c}`}>
                  <input id={`lt-ch-${c}`} type="checkbox" checked={form.channels.includes(c)}
                         onChange={() => setForm(f => ({ ...f, channels: toggle(f.channels, c) }))} />
                  <span>{CHANNEL_LABEL[c] || c}</span>
                </label>
              ))}
            </div>
            {noChannels && <p className="form-error" role="alert">Choose at least one channel.</p>}
          </fieldset>
          <div className="form-row cols-3">
            <Field label="Messages per user" htmlFor="lt-msgs">
              <input id="lt-msgs" className="form-input" type="number" min={1} max={999} step={1}
                     value={form.msgs_per_user} onChange={set('msgs_per_user')} />
            </Field>
            <Field label="Sent over (s)" htmlFor="lt-msgs-window"
                   hint="At random times in this window. A short one queues them behind proxy-channels.">
              <input id="lt-msgs-window" className="form-input" type="number" min={0} step={1}
                     value={form.msgs_window_s} onChange={set('msgs_window_s')} />
            </Field>
            <Field label="Wait for answers (s)" htmlFor="lt-drain"
                   hint="After the window, how long to wait for the last answers.">
              <input id="lt-drain" className="form-input" type="number" min={30} step={1}
                     value={form.drain_timeout_s} onChange={set('drain_timeout_s')} />
            </Field>
          </div>
        </>
      )}

      {stopAfter >= 3 && (
        <>
          <FormSection n={3} title="Tools" />
          <fieldset className="lt-fieldset">
            <legend>Tools to ask for</legend>
            <p className="text-muted">Granted to the test users. Never RossGate. The web tools read a built-in test site, not the Internet.</p>
            <div className="lt-options lt-options-grid">
              {(status?.tools || []).map(t => (
                <label key={t.id} className="checkbox-row" htmlFor={`lt-tool-${t.id}`}>
                  <input id={`lt-tool-${t.id}`} type="checkbox" checked={form.tools.includes(t.id)}
                         onChange={() => setForm(f => ({ ...f, tools: toggle(f.tools, t.id) }))} />
                  <span>{t.name} <span className="text-muted mono">{t.id}</span></span>
                </label>
              ))}
            </div>
            {noTools && <p className="form-error" role="alert">Choose a tool, or send no tool messages.</p>}
          </fieldset>
          <div className="form-row cols-3">
            <Field label="Tool messages per user" htmlFor="lt-tmsgs" hint="Each asks for a random chosen tool.">
              <input id="lt-tmsgs" className="form-input" type="number" min={0} max={999} step={1}
                     value={form.tool_msgs_per_user} onChange={set('tool_msgs_per_user')} />
            </Field>
            <Field label="Reminders per user" htmlFor="lt-crons"
                   hint={`One-shot reminders ${status?.reminder_minutes || 2} minutes ahead, through proxy-crons. At most ${status?.max_crons_per_user || 3}.`}>
              <input id="lt-crons" className="form-input" type="number" min={0}
                     max={status?.max_crons_per_user || 3} step={1}
                     value={form.crons_per_user} onChange={set('crons_per_user')} />
            </Field>
            <Field label="Workflows per user" htmlFor="lt-wf" hint="Background workflows the agent starts.">
              <input id="lt-wf" className="form-input" type="number" min={0} step={1}
                     value={form.workflows_per_user} onChange={set('workflows_per_user')} />
            </Field>
          </div>
          <div className="form-row cols-3">
            <Field label="Sent over (s)" htmlFor="lt-tools-window" hint="At random times in this window.">
              <input id="lt-tools-window" className="form-input" type="number" min={0} step={1}
                     value={form.tools_window_s} onChange={set('tools_window_s')} />
            </Field>
          </div>
        </>
      )}

      <FormSection title="The run" />
      <div className="form-row cols-3">
        <Field label="Hold (s)" htmlFor="lt-hold"
               hint="How long to keep the cohort up after the last phase.">
          <input id="lt-hold" className="form-input" type="number" min={0} step={1}
                 value={form.hold_s} onChange={set('hold_s')} />
        </Field>
        <Field label="Spend cap (€)" htmlFor="lt-cap" hint="Stop the run at this spend. Empty: no cap.">
          <input id="lt-cap" className="form-input" type="number" min={0} step="0.01"
                 value={form.spend_cap_eur} onChange={set('spend_cap_eur')} />
        </Field>
      </div>

      <label className="checkbox-row" htmlFor="lt-keep">
        <input id="lt-keep" type="checkbox" checked={form.keep} onChange={set('keep')} />
        <span>Keep the users, agents and workspaces after the run, until Clean up is pressed</span>
      </label>

      <div className="modal-footer">
        <button type="submit" className="btn btn-primary" disabled={disabled || busy || noChannels || noTools}
                aria-disabled={disabled || busy || noChannels || noTools}>
          {busy ? <span className="spinner-sm" aria-hidden="true" /> : null}
          Start the run
        </button>
      </div>
      {confirmDialog}
    </form>
  );
}

/* ── Runs list ──────────────────────────────────────────────────────────── */

function RunsList({ onOpen, onRerun }) {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(async () => {
    try {
      const body = await readJson(await apiFetch('/api/load-tests'));
      setRuns(body.runs || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);
  usePolling(load, 15000, [load]);

  const act = async (run, path, method, label, question) => {
    if (question && !await confirm(question)) return;
    setBusy(run.id);
    await callApi(`/api/load-tests/${run.id}${path}`, { method },
                  { successMsg: label, errorMsg: `${label}: failed` });
    setBusy(null);
    load();
  };

  if (runs === null && !error) return <TableSkeleton rows={4} cols={6} label="Loading runs" />;
  if (error && runs === null) {
    return <EmptyState icon="alertCircle" title="The runs did not load">{error}</EmptyState>;
  }
  if (!runs.length) return <EmptyState icon="gauge" title="No load tests yet" />;

  return (
    <div className="card card-flush">
      <div className="card-header">
        <h2 className="card-title">Runs</h2>
        <span className="text-sm text-muted">{formatNumber(runs.length)} on record</span>
      </div>
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              <th>Name</th><th>Status</th><th>Phases</th><th className="num">Users</th><th>Started by</th>
              <th>Created</th><th>Duration</th><th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {runs.map(r => {
              const final = !ACTIVE.includes(r.status);
              const stopAfter = r.params?.stop_after || 1;
              return (
                <tr key={r.id}>
                  {/* The name opens the report, as a user's name opens the user. */}
                  <td data-label="Name">
                    <Link to={{ search: `?run=${r.id}` }} className="cell-primary">{r.name}</Link>
                  </td>
                  <td data-label="Status">
                    <span className="row gap-2 nowrap">
                      <StatusBadge status={r.status} />
                      {/* As in the report's header: the reason only when it is not the plain end. */}
                      {r.end_reason && r.end_reason !== 'finished' && (
                        <span className="text-sm text-muted">{REASON_LABEL[r.end_reason] || r.end_reason}</span>
                      )}
                    </span>
                  </td>
                  <td data-label="Phases">{PHASES.slice(0, stopAfter).map(p => p.label).join(', ')}</td>
                  <td data-label="Users" className="num">{formatNumber(r.params?.users || 0)}</td>
                  <td data-label="Started by">{r.started_by}</td>
                  <td data-label="Created" className="nowrap">
                    <span className="num" title={fmtWhen(r.created_at)}>{runDate(r.created_at)}</span>
                  </td>
                  <td data-label="Duration" className="nowrap">{duration(r.started_at, r.ended_at)}</td>
                  {/* The table's own slots, as the Users and Broadcast tables: the report, Run
                      again, the action the run's state allows, then Delete, so each lines up
                      row to row. Run again only fills New run in: nothing starts from here. */}
                  <td data-label="Actions">
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'auto auto 1fr auto', gap: 'var(--sp-1)',
                      alignItems: 'center',
                    }}>
                      <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => onOpen(r.id)}
                              aria-label={`Open the report of ${r.name}`} title="Open the report">
                        <Icon name="eye" />
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => onRerun(r)}
                              aria-label={`Run ${r.name} again: load its settings into New run`}
                              title="Run again: load its settings into New run">
                        <Icon name="refresh" />
                      </button>
                      {r.active ? (
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy === r.id}
                                onClick={() => act(r, '/stop', 'POST', 'Stop requested', {
                                  title: 'Stop this run?',
                                  body: 'No more users or messages are started, and the cleanup begins.',
                                  confirmLabel: 'Stop the run', tone: 'danger' })}>
                          <Icon name="stop" /> Stop
                        </button>
                      ) : !r.cleaned ? (
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy === r.id}
                                onClick={() => act(r, '/cleanup', 'POST', 'Cleanup started', {
                                  title: 'Clean up this run?',
                                  body: 'Its users, agents and workspaces are deleted. The report stays.',
                                  confirmLabel: 'Clean up', tone: 'danger' })}>
                          <Icon name="sparkles" /> Clean up
                        </button>
                      ) : <span />}
                      {final && r.cleaned ? (
                        <button type="button" className="btn btn-danger-soft btn-sm btn-icon" disabled={busy === r.id}
                                aria-label={`Delete ${r.name}`} title="Delete the run and its report"
                                onClick={() => act(r, '', 'DELETE', 'Run deleted', {
                                  title: 'Delete this run?',
                                  body: 'Its report and the metrics stored with it are deleted for good.',
                                  confirmLabel: 'Delete', tone: 'danger' })}>
                          <Icon name="trash" />
                        </button>
                      ) : <span />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {confirmDialog}
    </div>
  );
}

/* ── The report ─────────────────────────────────────────────────────────────
   A run's page reads top down (Mark, 2026-10-18):
     the header      what ran, and a badge for the checks, spelled out when one failed;
     the recap       the figures that say at once how it went (`report.kpis`);
     the phases      when each ran, the legend for the strip on every chart;
     Dashboard       the Dashboard's own charts, a section per phase the run reached,
                     with the pods' and nodes' way up first;
     Platform        each service as the Platform page shows it, the list on the left,
                     the copies menu at the top right, and the database.
   Every time chart draws the phases on a strip above its plot (charts/phases.jsx). */

const RAMP_LABEL = { linear: 'linear', gaussian: 'gaussian', burst: 'all at once' };

// What the two parts of a phase are called where its sending ended, in the tooltips.
const PHASE_PARTS = {
  1: { during: 'users being requested', after: 'waiting for the last agents' },
  2: { during: 'messages being sent', after: 'waiting for the last answers' },
  3: { during: 'messages being sent', after: 'waiting for the last answers' },
};

const REPORT_TABS = [
  { value: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { value: 'platform', label: 'Platform', icon: 'infrastructure' },
];

// Counts in full, grouped the same way whatever the browser's language: "5,896 / 15,900".
const count = n => Number(n || 0).toLocaleString('en-US');

// A duration as a recap figure, short enough for two to sit side by side in a tile:
// 820 ms, 4.2 s, 42 s, 1m 18s, 1h 4m.
function fmtDur(v) {
  const n = Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return '—';
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 9950) return `${(n / 1000).toFixed(1)} s`;
  const s = Math.round(n / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 60)}h`;
}

function useWidth(initial = 800) {
  const ref = useRef(null);
  const [width, setWidth] = useState(initial);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = (w) => {
      const px = Math.round(w);
      if (px > 0) setWidth(prev => (Math.abs(prev - px) > 1 ? px : prev));
    };
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => measure(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/* Two columns of one grid row ending level: `beside` holds a card, `stack` cards one
   under the other, and the chart in the stack's last card is drawn at the height that
   brings its content down to where the card beside it ends. Each is measured by where its
   content ends, since the grid stretches both columns to the row. Side by side only: on a
   phone, one under the other, the chart keeps `base`. */
function useLevelWith(base, min = 200, max = 640) {
  const besideRef = useRef(null);
  const stackRef = useRef(null);
  const [height, setHeight] = useState(base);
  useLayoutEffect(() => {
    const beside = besideRef.current;
    const stack = stackRef.current;
    if (!beside || !stack) return undefined;
    const contentEnd = (card) => {
      const last = card?.lastElementChild;
      if (!last) return null;
      return last.getBoundingClientRect().bottom
        + (parseFloat(window.getComputedStyle(card).paddingBottom) || 0);
    };
    const fit = () => {
      const a = contentEnd(beside.lastElementChild);
      const b = contentEnd(stack.lastElementChild);
      if (a === null || b === null) return;
      const level = Math.abs(beside.getBoundingClientRect().top - stack.getBoundingClientRect().top) < 2;
      setHeight(prev => (level ? Math.max(min, Math.min(max, Math.round(prev + a - b))) : base));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => fit());
    ro.observe(beside);
    ro.observe(stack);
    return () => ro.disconnect();
  }, [base, min, max]);
  return [besideRef, stackRef, height];
}

function CheckList({ checks, id }) {
  return (
    <ul className="lt-checks" id={id}>
      {checks.map(c => (
        <li key={c.key}>
          <Badge tone={c.ok === true ? 'ok' : c.ok === false ? 'danger' : 'neutral'}>
            {c.ok === true ? 'Pass' : c.ok === false ? 'Fail' : 'Pending'}
          </Badge>{' '}
          <strong>{c.label}</strong> <span className="text-muted">{c.detail}</span>
        </li>
      ))}
    </ul>
  );
}

function PctTable({ rows, first, firstKey, extra = [] }) {
  if (!rows?.length) return <p className="text-muted">Nothing recorded.</p>;
  // Rows that carry no timing at all (what was asked, by kind) get no columns of dashes.
  const timed = rows.some(r => 'p50' in r || 'max' in r);
  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr><th>{first}</th>{extra.map(x => <th key={x.key} className="text-right">{x.label}</th>)}
            {timed && (
              <>
                <th className="text-right">p50</th><th className="text-right">p90</th>
                <th className="text-right">p99</th><th className="text-right">Max</th>
              </>
            )}</tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r[firstKey]}>
              <td className="mono">{r[firstKey]}</td>
              {extra.map(x => <td key={x.key} className="text-right num">{count(r[x.key])}</td>)}
              {timed && (
                <>
                  <td className="text-right num">{ms(r.p50)}</td><td className="text-right num">{ms(r.p90)}</td>
                  <td className="text-right num">{ms(r.p99)}</td><td className="text-right num">{ms(r.max)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* Percentiles of several measures, grouped: [{ title?, rows: [{ label, row, help?, strong? }] }].
   A measure nothing was recorded for is left out, except a group's total. */
function TimingTable({ groups }) {
  const shown = groups
    .map(g => ({ ...g, rows: g.rows.filter(r => r.strong || (r.row?.samples || 0) > 0) }))
    .filter(g => g.rows.length);
  return (
    <div className="table-container">
      <table className="table lt-timings">
        <thead>
          <tr><th>Measure</th><th className="text-right">p50</th><th className="text-right">p90</th>
            <th className="text-right lt-p99">p99</th><th className="text-right">Max</th></tr>
        </thead>
        {shown.map(g => (
          <tbody key={g.title || 'all'}>
            {g.title && (
              <tr className="lt-timings-group"><td colSpan={5}>{g.title}</td></tr>
            )}
            {g.rows.map(r => (
              <tr key={r.label} className={r.strong ? 'lt-timings-total' : undefined}>
                <td>
                  <span className="row gap-1 nowrap">
                    {r.label}{r.help && <InfoTip label={r.label}>{r.help}</InfoTip>}
                  </span>
                </td>
                <td className="text-right num">{ms(r.row?.p50)}</td>
                <td className="text-right num">{ms(r.row?.p90)}</td>
                <td className="text-right num lt-p99">{ms(r.row?.p99)}</td>
                <td className="text-right num">{ms(r.row?.max)}</td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

// Why a user's pod waited, as Kubernetes last said it, in words: a user whose pod waited
// for a node and then answered did not have a problem, so the table says what it cost.
const WAIT_TEXT = {
  Unschedulable: 'waited for a node', ImagePullBackOff: 'image pull retried',
  ErrImagePull: 'image pull retried', ContainerCreating: 'slow to create its container',
  CrashLoopBackOff: 'crash looping', CreateContainerConfigError: 'container config error',
};
const waitText = reason => WAIT_TEXT[reason] || reason;

const USERS_PAGE = 50;
const NO_USERS = { total: 0, items: [] };

function UsersTable({ runId }) {
  const [data, setData] = useState(NO_USERS);
  const [page, setPage] = useState(1);
  useEffect(() => {
    // Only the page asked for last: pages clicked through quickly can answer out of order.
    let current = true;
    apiFetch(`/api/load-tests/${runId}/users?offset=${(page - 1) * USERS_PAGE}&limit=${USERS_PAGE}`)
      .then(readJson)
      .then((d) => { if (current) setData(d); })
      .catch(() => { if (current) setData(NO_USERS); });
    return () => { current = false; };
  }, [runId, page]);
  if (!data.items.length) return null;
  return (
    <>
      <div className="section-head">
        <h2>Users</h2>
        <p>each test user, its pod and what came of it</p>
      </div>
      <div className="card card-flush">
        <div className="card-header">
          <h2 className="card-title">Test users</h2>
          <span className="text-sm text-muted">{count(data.total)} in all</span>
        </div>
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th>#</th><th>User</th><th>Channel</th><th>Outcome</th><th>Provision request</th>
                <th>Deploy time</th><th>Node</th><th>Note</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(u => (
                <tr key={u.seq}>
                  <td data-label="#">{u.seq}</td>
                  <td data-label="User" className="mono">{u.username}</td>
                  <td data-label="Channel">{CHANNEL_LABEL[u.channel] || u.channel || '—'}</td>
                  <td data-label="Outcome">{u.outcome}</td>
                  <td data-label="Provision request">
                    {u.provision_requested_at && u.provision_returned_at
                      ? formatMs(new Date(u.provision_returned_at) - new Date(u.provision_requested_at)) : '—'}
                    {u.provision_http && u.provision_http !== 200 ? ` (HTTP ${u.provision_http})` : ''}
                  </td>
                  <td data-label="Deploy time">{ms(u.time_to_answer_ms)}</td>
                  {/* The host part, as the nodes table names it; the full name on hover. */}
                  <td data-label="Node" className="mono nowrap" title={u.node || undefined}>
                    {u.node ? String(u.node).split('.')[0] : '—'}
                  </td>
                  <td data-label="Note" className="wrap">
                    {u.error ? <span className="text-danger">{u.error}</span>
                      : u.waiting_reason ? <span className="text-muted">{waitText(u.waiting_reason)}</span>
                        : u.restarts ? `${u.restarts} restarts` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* The tables' own pager, as on Users and Events. */}
        <Pagination page={page} pageSize={USERS_PAGE} total={data.total} onPage={setPage} noun="users" />
      </div>
    </>
  );
}

/* The report's own rows are labelled on Rome's clock, "YYYY-MM-DD HH:MM:SS" (the
   backend's `_label`); the Platform's are keyed by an ISO time, read on the same clock. */
const ROME_LABEL = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});
const platformX = (iso) => ROME_LABEL.format(new Date(iso)).slice(11, 16);
// To the second when the buckets are shorter than a minute.
const clockX = (step) => (s) => (s || '').slice(11, step >= 60 ? 16 : 19);
const romeClock = (v) => (v ? ROME_LABEL.format(new Date(v)).slice(11, 19) : '—');

// Who made an LLM call (proxy-llm's X-Source), named as the Dashboard's Source filter.
const sourceName = (s) => (s === 'llm' ? 'User' : s === 'tools' ? 'External Tool'
  : String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1));

// The services a run uses, in the order a message goes through them.
const SERVICE_ORDER = ['control-plane', 'telegram-bridge', 'website-bridge', 'app-bridge',
  'proxy-channels', 'proxy-llm', 'proxy-tools', 'proxy-crons'];
const serviceRank = (id) => {
  const i = SERVICE_ORDER.indexOf(id);
  return i < 0 ? SERVICE_ORDER.length : i;
};
const KIND_RANK = { proxy: 0, bridge: 1, core: 2 };
const kindOf = id => (id.includes('bridge') ? 'bridge' : id.startsWith('proxy-') ? 'proxy' : 'core');

// Container Insights' retention: past it a run's copies cannot be read
// (backend loadtest/platform_views.CI_RETENTION).
const CI_RETENTION_MS = 14 * 86400 * 1000;
// How long a finished run's report is read again for its Platform views: three minutes of
// CloudWatch settling and the reading itself, with room to spare. They are read once per
// console process, so waiting longer has nothing to wait for.
const VIEWS_WAIT_MS = 15 * 60 * 1000;

/* Whether the Platform's views of a finished run are still to come: the console reads
   them from CloudWatch three minutes after the run's end, on a read of this report
   (services/load_tests.store_views_later), so the page keeps reading until they are in. */
function viewsPending(report) {
  if (!report || ACTIVE.includes(report.run.status)) return false;
  const st = report.general?.views_status || {};
  const started = report.run.started_at ? Date.parse(report.run.started_at) : NaN;
  return !st.error && st.views === undefined && Date.now() - started < CI_RETENTION_MS;
}

/* What the Platform tab shows, and why, when it is not the Platform's own view of the
   run. `waiting`: the page is still reading the report for them. */
function viewsNote(report, waiting) {
  const { run } = report;
  const g = report.general || {};
  const st = g.views_status || {};
  const services = Object.values(g.services_view || {});
  const kept = services.some(v => v?.legacy)
    ? ' Below, the series the run kept: each service as a whole, without its copies.' : '';
  if (ACTIVE.includes(run.status)) {
    return 'The Platform\'s views of the services, per copy, and of the database are read from CloudWatch three minutes after the run ends.';
  }
  if (st.error) return `The Platform's views of the run were not stored: ${st.error}.${kept}`;
  if (st.views !== undefined) {
    return st.views ? null : `The Platform had no view of this run's services.${kept}`;
  }
  if (viewsPending(report)) {
    const lost = !services.length && g.metrics_status?.error
      ? ` The run's own series were not stored either: ${g.metrics_status.error}.` : '';
    return waiting
      ? `The Platform's views of the run are read from CloudWatch three minutes after it ends, and this page picks them up by itself.${kept}${lost}`
      : `The Platform's views of the run are not in yet. Reload the page in a few minutes.${kept}${lost}`;
  }
  return `Container Insights keeps 14 days and this run is older, so its copies cannot be read.${kept}`;
}

/* ── The header ── */

/* The checks, folded into one badge: the recap says how the run went, and a check that
   failed is spelled out above, so only the passes are folded away. */
function Verdict({ checks }) {
  const [open, setOpen] = useState(false);
  if (!checks?.length) return null;
  const failed = checks.filter(c => c.ok === false).length;
  const pending = checks.filter(c => c.ok !== true && c.ok !== false).length;
  const passed = checks.length - failed - pending;
  const [tone, text] = failed ? ['danger', `${failed} of ${checks.length} checks failed`]
    : pending ? ['info', `${passed} of ${checks.length} checks passed so far`]
      : ['ok', `All ${checks.length} checks passed`];
  return (
    <div className="lt-verdict">
      <button type="button" className="lt-verdict-toggle" aria-expanded={open}
              aria-controls="ltr-checks" onClick={() => setOpen(o => !o)}>
        <Badge tone={tone} dot>{text}</Badge>
        <span className="lt-verdict-more">{open ? 'Hide the checks' : 'Show the checks'}</span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={14} />
      </button>
      {open && <CheckList checks={checks} id="ltr-checks" />}
    </div>
  );
}

function RunHeader({ report, onBack, onStop }) {
  const { run } = report;
  const p = run.params || {};
  const stopAfter = p.stop_after || 1;
  const spent = report.kpis?.llm?.cost;
  const failed = (report.checks || []).filter(c => c.ok === false);
  const setup = [
    `${count(p.users)} users`,
    p.llm === 'mock'
      ? `the mock model on ${p.tier_name} (${p.mock_ttft_ms} ms to first token, ${p.mock_tokens_per_s} tokens/s)`
      : `${p.tier_name}, its real model`,
    PHASES.slice(0, stopAfter).map(x => x.label).join(', '),
    stopAfter >= 2 ? (p.channels || []).map(c => CHANNEL_LABEL[c] || c).join(', ') : '',
    `deployed over ${p.deploy_window_s} s, ${RAMP_LABEL[p.ramp] || p.ramp}`,
    `held ${p.hold_s} s`,
    p.keep ? 'kept after the run' : '',
  ].filter(Boolean);
  const when = [
    `Started ${fmtWhen(run.started_at)} by ${run.started_by}`,
    `ran ${duration(run.started_at, run.ended_at)}`,
    spent ? `${formatCost(spent)} of LLM spend` : '',
  ].filter(Boolean);
  return (
    <div className="card lt-run-head">
      <div className="lt-run-head-top">
        <div className="min-w-0">
          <div className="row row-wrap gap-2">
            <h2 className="lt-run-title">{run.name}</h2>
            <StatusBadge status={run.status} />
            {run.end_reason && run.end_reason !== 'finished' && (
              <span className="text-sm text-muted">{REASON_LABEL[run.end_reason] || run.end_reason}</span>
            )}
          </div>
          <p className="lt-run-meta">{setup.join(' · ')}</p>
          <p className="lt-run-meta">{when.join(' · ')}</p>
        </div>
        <div className="btn-group">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>
            <Icon name="chevronLeft" size={14} />
            All runs
          </button>
          {run.active && (
            <button type="button" className="btn btn-danger-soft btn-sm" onClick={onStop}>Stop</button>
          )}
        </div>
      </div>
      {failed.length > 0 && (
        <div className="notice notice-danger mt-3" role="status">
          <div className="notice-body">
            <strong>What went wrong</strong>
            <ul className="lt-failed">
              {failed.map(c => <li key={c.key}>{c.label}: {c.detail}</li>)}
            </ul>
          </div>
        </div>
      )}
      {run.error && (
        <div className="notice notice-danger mt-3" role="status">
          <div className="notice-body">{run.error}</div>
        </div>
      )}
      <Verdict checks={report.checks} />
    </div>
  );
}

/* ── The recap ── */

/* One figure, in the Dashboard's KPI tile: a value (with a trailing unit), or for a
   duration the median beside the slowest. */
function Kpi({ icon, label, help, value, unit, max, sub, tone, span }) {
  const toneClass = tone === 'positive' ? 'text-ok' : tone === 'negative' ? 'text-danger'
    : tone === 'warning' ? 'text-warn' : undefined;
  return (
    <div className={cx('card', 'dashboard-card', 'kpi-tile', `dashboard-tile-${span}`)}>
      <div className="kpi-label">
        {icon && <Icon name={icon} size={14} />}
        <span>{label}</span>
        {help && <InfoTip label={label} width={320}>{help}</InfoTip>}
      </div>
      {max !== undefined ? (
        <div className="kpi-figures">
          <div>
            <div className={cx('kpi-value', toneClass)}>{value}</div>
            <div className="kpi-caption">median</div>
          </div>
          <div>
            <div className="kpi-value kpi-value-2">{max}</div>
            <div className="kpi-caption">max</div>
          </div>
        </div>
      ) : (
        <div className={cx('kpi-value', toneClass)}>
          {value}{unit && <span className="kpi-unit"> {unit}</span>}
        </div>
      )}
      {sub && <div className="kpi-meta"><span>{sub}</span></div>}
    </div>
  );
}

// A figure that is all or nothing: green when every one made it, red when some had not
// by the end; no colour while the run still goes.
const whole = (ok, of, final) => (!final || !of ? undefined : ok >= of ? 'positive' : 'negative');

function problemText(pp) {
  return [
    [pp.restarts, 'restarts'], [pp.oom_killed, 'OOM kills'], [pp.preempted, 'preempted'],
    [pp.evicted, 'evicted'], [pp.pulls_failed, 'failed pulls'],
  ].filter(([n]) => n).map(([n, what]) => `${count(n)} ${what}`).join(', ');
}

function onboardingKpis(k, final) {
  const a = k.agents || {};
  const d = k.deploy || {};
  const nodes = k.nodes || {};
  const ready = nodes.ready || {};
  const pp = k.pod_problems || {};
  const missing = Math.max(0, (a.users || 0) - (a.answering || 0));
  return [
    { key: 'agents', icon: 'bot', label: 'Agents answering', value: count(a.answering),
      unit: `/ ${count(a.users)}`, tone: whole(a.answering, a.users, final),
      sub: a.failed ? `${count(a.failed)} failed`
        : missing ? `${count(missing)} ${final ? 'never answered' : 'still coming up'}`
          : 'every agent came up',
      help: 'The agents that came up and asked for their first message, of the users the run created.' },
    { key: 'deploy', icon: 'clock', label: 'Pod deploy time', value: fmtDur(d.p50),
      max: fmtDur(d.max),
      sub: d.samples ? `p90 ${fmtDur(d.p90)} · ${count(d.samples)} pods` : 'no agent answering yet',
      help: 'From the provision request until the agent in its new pod asked for its first message, which is when it can answer: the median and the slowest.' },
    nodes.added ? {
      key: 'nodes', icon: 'server', label: 'New node time', value: fmtDur(ready.p50),
      max: fmtDur(ready.max),
      // One line in the tile, as the others: the help says what the pods waited for.
      sub: `${count(nodes.added)} ${nodes.added === 1 ? 'node' : 'nodes'} added · ${count(nodes.pods_waited)} ${nodes.pods_waited === 1 ? 'pod' : 'pods'} waited`,
      help: 'From Karpenter requesting a node until the node was Ready for pods, over every node the run made it add: for the agents that found no room, and to restore the warm buffer they took. Pods waited: the agent pods that waited for one of them.',
    } : {
      key: 'nodes', icon: 'server', label: 'New node time', value: 'None needed',
      sub: 'no pod waited for a node',
      help: 'From Karpenter requesting a node until the node was Ready for pods. This run needed no new node: the agents fitted on the nodes and the warm buffer already there.',
    },
    { key: 'problems', icon: 'alert', label: 'Pod problems', value: count(pp.total),
      tone: pp.total ? 'warning' : final ? 'positive' : undefined,
      sub: pp.total ? problemText(pp) : 'no restart, eviction or OOM kill',
      help: 'Restarts, OOM kills, preemptions, evictions and image pulls that failed, over the pods of the run.' },
  ];
}

function messageKpis(k, final, real) {
  const m = k.messages;
  const t = k.ttft || {};
  const r = k.reply || {};
  const llm = k.llm || {};
  return [
    { key: 'messages', icon: 'message', label: 'Messages answered',
      value: m ? count(m.answered) : '—', unit: m ? `/ ${count(m.sent)}` : undefined,
      tone: m ? whole(m.answered, m.sent, final) : undefined,
      sub: !m ? 'not started' : m.waiting ? `${count(m.waiting)} ${final ? 'never answered' : 'waiting'}`
        : m.sent ? 'every message answered' : 'none sent yet',
      help: 'Phases 2 and 3 together. A user\'s second message counts as answered once that user has had two replies, and so on: an agent answers its messages one at a time.' },
    real && {
      key: 'ttft', icon: 'zap', label: 'Time to first token', value: fmtDur(t.p50),
      max: fmtDur(t.max), sub: t.samples ? `p90 ${fmtDur(t.p90)} · ${count(t.samples)} LLM calls` : 'no LLM call yet',
      help: 'The agents\' own LLM calls, the ones a user waits on: from the request until the model\'s first token.',
    },
    { key: 'reply', icon: 'activity', label: 'Reply time', value: fmtDur(r.p50), max: fmtDur(r.max),
      sub: r.samples ? `p90 ${fmtDur(r.p90)} · to the next reply` : 'no reply yet',
      help: 'From the worker handing a message to the bridge until the next reply reached that user\'s channel: how long a user waited to hear anything.' },
    { key: 'llm', icon: 'alertCircle', label: 'LLM errors', value: count(llm.errors),
      tone: llm.errors ? 'negative' : final && llm.calls ? 'positive' : undefined,
      sub: `of ${count(llm.calls)} LLM calls${llm.cost ? ` · ${formatCost(llm.cost)} spent` : ''}`,
      help: 'Calls proxy-llm refused or that failed, of every call it served the test users: the agents\', the tools\', the outbound correction\'s and speech.' },
  ].filter(Boolean);
}

function toolKpis(k, final) {
  const t = k.tools;
  const lat = t?.latency || {};
  const rem = k.reminders;
  return [
    { key: 'tools', icon: 'tools', label: 'Tool calls succeeded',
      value: t ? count(t.invoked - t.failed) : '—', unit: t ? `/ ${count(t.invoked)}` : undefined,
      tone: t && t.invoked ? (t.failed ? 'warning' : final ? 'positive' : undefined) : undefined,
      sub: !t ? 'not started' : t.failed ? `${count(t.failed)} failed` : t.invoked ? 'none failed' : 'none called yet',
      help: 'Every call proxy-tools ran for the test users in phase 3, the outbound correction\'s included.' },
    { key: 'toollat', icon: 'clock', label: 'Tool call latency', value: fmtDur(lat.p50),
      max: fmtDur(lat.max), sub: lat.samples ? `p90 ${fmtDur(lat.p90)} · successful calls` : 'no call succeeded yet',
      help: 'proxy-tools\' own time for each call that succeeded, as the Dashboard\'s Tool call latency.' },
    rem && rem.asked > 0 && {
      key: 'reminders', icon: 'bell', label: 'Reminders delivered', value: count(rem.delivered),
      unit: `/ ${count(rem.asked)}`, tone: whole(rem.delivered, rem.asked, final),
      sub: rem.delivered >= rem.asked ? 'every reminder fired'
        : `${count(rem.asked - rem.delivered)} ${final ? 'never fired' : 'still due'}`,
      help: 'A message reached the user at or after the time the reminder was set for: the agent\'s own scheduler fired it.',
    },
  ].filter(Boolean);
}

/* The figures that say at once how the run went, a row per phase it reached. */
function RunKpis({ report }) {
  const k = report.kpis;
  if (!k) return null;
  const { run } = report;
  const p = run.params || {};
  const stopAfter = p.stop_after || 1;
  const final = !ACTIVE.includes(run.status);
  const rows = [onboardingKpis(k, final)];
  if (stopAfter >= 2) rows.push(messageKpis(k, final, p.llm === 'real'));
  if (stopAfter >= 3) rows.push(toolKpis(k, final));
  return (
    <section className="lt-kpis" aria-label="The run at a glance">
      {rows.map(row => (
        <div key={row[0].key} className="dashboard-grid">
          {row.map(({ key, ...t }) => <Kpi key={key} span={12 / row.length} {...t} />)}
        </div>
      ))}
    </section>
  );
}

/* ── The phases ── */

/* A span's key, as the strips draw it: a phase's number on its tint, the hold and the
   cleanup a grey swatch. */
function PhaseChip({ span, large = false }) {
  const phase = span.kind === 'phase';
  return (
    <span className={cx('lt-phase-num', !phase && 'is-neutral', large && 'lt-phase-num-lg')}
          aria-hidden="true">
      {phase ? span.n : ''}
    </span>
  );
}

function outcomeOf(s) {
  if (s.kind !== 'phase') return '';
  if (!s.end) return 'running';
  return s.outcome ? (REASON_LABEL[s.outcome] || s.outcome) : 'Finished';
}

/* The run's spans as every chart below draws them, the legend for their strips, with
   when each began, stopped sending and ended. */
function RunTimeline({ run, timeline }) {
  const [ref, width] = useWidth();
  const t0 = Date.parse(run.started_at);
  const tEnd = run.ended_at ? Date.parse(run.ended_at) : Date.now();
  const total = Math.max(1, tEnd - t0);
  const spans = timeline.filter(s => s.start < tEnd);
  const x = t => ((Math.min(Math.max(t, t0), tEnd) - t0) / total) * width;
  const segments = spans.map(s => ({
    ...s, x0: x(s.start), x1: x(s.end ?? tEnd), xs: s.sent ? x(s.sent) : null,
  }));
  return (
    <div className="card lt-timeline-card">
      <div className="card-header">
        <h2 className="card-title">Phases</h2>
        <span className="text-sm text-muted">
          {romeClock(t0)} to {romeClock(tEnd)} · {duration(run.started_at, run.ended_at)}
        </span>
      </div>
      <div ref={ref} className="lt-timeline">
        <svg width={width} height={28} viewBox={`0 0 ${width} 28`} aria-hidden="true">
          <PhaseRibbon segments={segments} y={0} h={28} fontSize={12}
                       title={s => `${s.label}: ${romeClock(s.start)} to ${s.end ? romeClock(s.end) : 'now'}`} />
        </svg>
      </div>
      {/* On a phone, a line per phase rather than a table stacked into cards. */}
      <ul className="lt-phase-list show-sm">
        {spans.map(s => (
          <li key={`${s.kind}-${s.n ?? ''}`}>
            <PhaseChip span={s} />
            <span className="lt-phase-list-text">
              <span className="lt-phase-list-name">{s.label}</span>
              <span className="lt-phase-list-meta">
                {romeClock(s.start)} to {s.end ? romeClock(s.end) : 'now'}
                {s.sent ? `, sending until ${romeClock(s.sent)}` : ''}
              </span>
            </span>
            <span className="lt-phase-list-dur">
              {duration(new Date(s.start), s.end ? new Date(s.end) : null)}
            </span>
          </li>
        ))}
      </ul>
      <div className="table-container hide-sm">
        <table className="table lt-phase-table">
          <thead>
            <tr>
              {/* The times are right-aligned figures, so are their headings. */}
              <th>Phase</th><th className="num">From</th>
              <th className="num">
                <span className="row row-end gap-1 nowrap">
                  Sending until
                  <InfoTip label="Sending until">
                    Phase 1: when the last user was requested. Phases 2 and 3: when the last
                    message went. After it the phase only waits for the last agents or answers.
                  </InfoTip>
                </span>
              </th>
              <th className="num">To</th><th>Duration</th><th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {spans.map(s => (
              <tr key={`${s.kind}-${s.n ?? ''}`}>
                <td data-label="Phase" className="cell-primary">
                  <span className="row gap-2 nowrap">
                    <PhaseChip span={s} />
                    {s.label}
                  </span>
                </td>
                <td data-label="From" className="num">{romeClock(s.start)}</td>
                <td data-label="Sending until" className="num">{s.sent ? romeClock(s.sent) : '—'}</td>
                <td data-label="To" className="num">{s.end ? romeClock(s.end) : 'now'}</td>
                <td data-label="Duration">{duration(new Date(s.start), s.end ? new Date(s.end) : null)}</td>
                <td data-label="Outcome">
                  {outcomeOf(s) && outcomeOf(s) !== 'Finished' && outcomeOf(s) !== 'running'
                    ? <Badge tone="warn">{outcomeOf(s)}</Badge> : outcomeOf(s)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="lt-timeline-note">
        Every chart below draws these phases on a strip at its top: the stronger part of a
        phase while users were requested or messages sent, the lighter part the wait for
        the last agents or answers.
      </p>
    </div>
  );
}

/* A phase's section in the Dashboard tab: its number, as on the strips, and its window. */
function PhaseHead({ n, title, phase, summary }) {
  const started = phase?.started_at;
  const span = started
    ? `${romeClock(started)} to ${phase.ended_at ? romeClock(phase.ended_at) : 'now'} · ${duration(started, phase.ended_at)}`
    : 'Not started';
  const outcome = phase?.end && phase.end !== 'finished' ? (REASON_LABEL[phase.end] || phase.end) : null;
  return (
    <div className="lt-phase-head">
      <PhaseChip span={{ kind: 'phase', n }} large />
      <div className="lt-phase-head-text">
        <h2><span className="sr-only">Phase {n}: </span>{title}</h2>
        <p>{span}{summary ? ` · ${summary}` : ''}</p>
      </div>
      {outcome && <Badge tone="warn">{outcome}</Badge>}
    </div>
  );
}

/* ── The Dashboard tab ── */

function RunOverview({ report, timeline }) {
  const g = report.general || {};
  const rows = g.rows || [];
  const userRows = report.series?.rows || [];
  const fmtX = clockX(report.series?.step_s || 60);
  const states = STATE_SERIES.filter(x => userRows.some(r => r[x.key]));
  const errors = rows.some(r => r.llm_errors);
  const traffic = [
    { key: 'sent', label: 'Sent', color: PALETTE[7] },
    { key: 'inbound', label: 'Inbound', color: PALETTE[3] },
    { key: 'outbound', label: 'Outbound', color: PALETTE[1] },
    { key: 'llm_calls', label: 'LLM calls', color: PALETTE[0] },
    { key: 'tool_calls', label: 'Tool calls', color: PALETTE[2] },
    { key: 'cron_calls', label: 'Scheduler calls', color: PALETTE[6] },
    ...(errors ? [{ key: 'llm_errors', label: 'LLM errors', color: PALETTE[4] }] : []),
  ];
  return (
    <>
      <div className="section-head">
        <h2>The whole run</h2>
        <p>where the users were, and the traffic, from the start to the cleanup</p>
      </div>
      <div className="dashboard-grid">
        <Card title="Users by state" span={6} subtitle="Every test user, every 5 seconds"
              help="Each test user's state as the worker saw it: waiting to start, provisioning, its pod pending or starting, answering, then hibernated or deleted by the cleanup. The stack's height is every user of the run."
              empty={states.length ? null : 'No user yet'}>
          <TimeChart stacked data={userRows} labelKey="date" formatX={fmtX} height={262} legend
                     lines={states.map(s => ({ key: s.key, label: s.label }))}
                     colors={states.map(s => s.color)} phases={phasesOn(userRows, timeline)} />
        </Card>
        <Card title="Traffic" span={6} subtitle="Messages and calls per bucket"
              help="Sent is the worker handing a message to the bridge. Inbound and outbound are proxy-channels' own records; LLM calls are proxy-llm's, tool calls proxy-tools', scheduler calls proxy-crons'. When outbound falls behind sent, the agents are behind."
              empty={rows.some(r => r.sent || r.inbound || r.llm_calls) ? null : 'No traffic yet'}>
          <TimeChart data={rows} labelKey="date" formatX={fmtX} phases={phasesOn(rows, timeline)} legend
                     lines={traffic.map(({ key, label }) => ({ key, label }))}
                     colors={traffic.map(l => l.color)} height={262} />
        </Card>
      </div>
    </>
  );
}

function onboardingTimings(report) {
  const s = report.summary;
  const groups = [
    { title: 'From the request to an answering agent', rows: [
      { label: 'Pod deploy time', row: s.time_to_answer, strong: true,
        help: 'From the provision request until the agent first asked for a message, which is when it can answer.' },
      { label: 'On capacity already there', row: s.time_to_answer_warm,
        help: 'Pods that found room at once: a node with space, or a balloon\'s place.' },
      { label: 'On a node added for it', row: s.time_to_answer_new_node,
        help: 'Pods that waited for a node Karpenter added for them: its launch, its join and the image pull.' },
    ] },
    { title: 'Step by step', rows: (report.pod_steps || []).map(st => ({ label: st.label, row: st, help: st.help })) },
    { title: 'Inside the control-plane', rows: [
      { label: 'User creation', row: s.user_create,
        help: 'Creating each user, as the website\'s sign-up and the external API do. A database under strain shows here first.' },
      { label: 'Provision request', row: s.provision_request,
        help: 'The control-plane\'s provision route, which answers once the agent is asked to start, before it is up.' },
      { label: 'Waiting for a thread', row: s.provision_queue,
        help: 'How long a provision request waited for one of the control-plane\'s provisioning threads.' },
      { label: 'Provisioning work', row: s.provision_work,
        help: 'The provisioning itself once it had a thread: the workspace and the Kubernetes objects.' },
    ] },
  ];
  if (s.nodes_added) {
    groups.push({ title: 'The nodes the run added', rows: [
      { label: 'Node Ready', row: s.node_ready,
        help: 'From the node\'s creation until Karpenter called it initialised.' },
      { label: 'First image pull', row: s.node_pull,
        help: 'The first agent-image pull on each new node.' },
    ] });
  }
  return groups;
}

/* How full the agent nodes were, the pool the run's pods wait on, and how much of its CPU
   limit Karpenter had used. A run recorded before the agent nodes' own figures has the
   whole cluster's CPU share only, which the general nodes' spare CPU dilutes (no agent can
   use it): it says so, since it reads low while the agent nodes are full. */
function CapacityCard({ rows, fmtX, phases }) {
  const has = key => rows.some(r => r[key] !== null && r[key] !== undefined);
  const own = has('agent_cpu_requested_pct');
  const lines = [
    ...(own
      ? [{ key: 'agent_cpu_requested_pct', label: 'CPU requested', color: PALETTE[3] },
         { key: 'agent_mem_requested_pct', label: 'Memory requested', color: PALETTE[1] }]
      : [{ key: 'cpu_requested_pct', label: 'CPU requested, every node', color: PALETTE[3] }]),
    ...(has('karpenter_limit_pct')
      ? [{ key: 'karpenter_limit_pct', label: 'Karpenter limit used', color: PALETTE[9] }] : []),
  ].filter(l => has(l.key));
  return (
    <Card title="Agent node capacity" span={6}
          subtitle={own ? 'Requested of the ready agent nodes, and Karpenter\'s limit'
            : 'CPU requested of every ready node, and Karpenter\'s limit'}
          help={own
            ? 'Requested: what the pods on the ready agent nodes reserve, of what those nodes offer; the general nodes are left out, since no agent can use them. Near 100% a new agent pod waits for a node. Karpenter limit used: the agent pool\'s CPU of its NodePool limit. At 100% Karpenter adds no node, and pods wait until others go.'
            : 'This run was recorded before the agent nodes\' own figures: the share is of every ready node, the general ones included, whose spare CPU no agent can use, so it reads low while the agent nodes are full. Karpenter limit used: the agent pool\'s CPU of its NodePool limit. At 100% Karpenter adds no node.'}
          empty={lines.length ? null : 'Not sampled'}>
      <TimeChart data={rows} labelKey="date" formatX={fmtX} phases={phases} legend gapOnNull
                 lines={lines.map(({ key, label }) => ({ key, label }))}
                 colors={lines.map(l => l.color)} height={262}
                 formatY={v => `${Math.round(v)}%`} formatValue={v => `${Number(v).toFixed(1)}%`}
                 refLines={[{ value: 100, label: '100%' }]} />
    </Card>
  );
}

function OnboardingSection({ report, timeline }) {
  const s = report.summary;
  const series = report.onboarding_series || report.series || {};
  const rows = series.rows || [];
  const fmtX = clockX(series.step_s || 60);
  const phases = phasesOn(rows, timeline);
  const p1 = (report.phase_list || []).find(p => p.n === 1);
  const limit = report.run.params?.max_in_flight;
  // A node's times (Rome, as the backend labels them) read as a clock on the run's day.
  const runDay = report.run.started_at
    ? ROME_LABEL.format(new Date(report.run.started_at)).slice(0, 10) : '';
  const onRunDay = label => (!label ? '—' : label.slice(0, 10) === runDay ? label.slice(11) : label);
  // The two charts beside the timings table end level with it.
  const [timingsRef, stackRef, offeredH] = useLevelWith(242);
  return (
    <>
      <PhaseHead n={1} title="Onboarding" phase={p1}
                 summary={`${count(s.answering)} of ${count(s.users)} agents answering`} />
      <div className="dashboard-grid">
        {/* The deployment step by step: how many of the run's pods, and of the nodes it
            added, had reached each step by then (load_test_pods, load_test_nodes). The
            gap between two lines is what waited at that step. */}
        <Card title="Pods of the run" span={6} subtitle="How many had reached each step"
              help="Users requested is the load the run offered. Then the pods of its users, as Kubernetes stamps them: created, scheduled on a node, running, Ready (the workspace probe), and answering (the agent's first poll for a message). The gap between two lines is the pods waiting at that step: for a node, the image, the probe."
              empty={rows.some(r => r.pods_created) ? null : 'No pod yet'}>
          <TimeChart data={rows} labelKey="date" formatX={fmtX} phases={phases} legend
                     lines={[{ key: 'requested_total', label: 'Users requested' },
                             { key: 'pods_created', label: 'Created' },
                             { key: 'pods_scheduled', label: 'Scheduled' },
                             { key: 'pods_running', label: 'Running' },
                             { key: 'pods_ready', label: 'Ready' },
                             { key: 'pods_answering', label: 'Answering' }]}
                     colors={[STAGE_COLOR.requested, STAGE_COLOR.created, STAGE_COLOR.scheduled,
                              STAGE_COLOR.running, STAGE_COLOR.ready, STAGE_COLOR.answering]}
                     height={262} />
        </Card>
        <Card title="Nodes the run added" span={6} subtitle="How many had reached each step"
              help="The nodes Karpenter added during the run, as their NodeClaims record them: requested, launched (the instance runs), registered (it joined the cluster), Ready (Karpenter called it initialised), and removed. Some restore the warm buffer the agents took, so they come last and hold no agent. Nodes that were there before the run are not counted."
              empty={rows.some(r => r.nodes_requested) ? null : 'The run added no node'}>
          <TimeChart data={rows} labelKey="date" formatX={fmtX} phases={phases} legend
                     lines={[{ key: 'nodes_requested', label: 'Requested' },
                             { key: 'nodes_launched', label: 'Launched' },
                             { key: 'nodes_registered', label: 'Registered' },
                             { key: 'nodes_ready', label: 'Ready' },
                             { key: 'nodes_removed', label: 'Removed' }]}
                     colors={[STAGE_COLOR.requested, STAGE_COLOR.scheduled, STAGE_COLOR.running,
                              STAGE_COLOR.ready, STAGE_COLOR.removed]}
                     height={262} />
        </Card>

        <Card title="Pod deploy time" span={12}
              subtitle="By when the user was requested: does the wait grow with the load?"
              help="Percentiles of the wait from the provision request until the agent first polled for messages, grouped by the bucket the request was sent in. A flat band means the platform kept up; a rising one means it fell behind."
              empty={latencyHasSamples(rows) ? null : 'No agent has answered yet'}>
          {/* A bucket in which no user was requested (its percentiles are null) is a gap,
              not a 0 ms answer. */}
          <PercentileChart data={rows} formatX={fmtX} phases={phases} gapOnNull height={262} />
        </Card>

        <div ref={timingsRef} className="dashboard-tile-6 lt-stack">
          <Card title="Where the time went"
                subtitle="From the provision request to an answering agent"
                help="Kubernetes stamps the steps to the second. Waiting for capacity is long when a node had to be added; the control-plane's part is its queue and its work.">
            <TimingTable groups={onboardingTimings(report)} />
          </Card>
        </div>
        {/* Two charts stacked beside the table: the second is drawn as tall as it takes to
            end level with the table, so neither column ends in an empty stretch of card. */}
        <div ref={stackRef} className="dashboard-tile-6 lt-stack">
          <Card title="Pod deploy time, distribution"
                empty={report.histogram.length ? null : 'No agent has answered yet'}>
            <Histogram data={report.histogram} unit="users"
                       colors={report.histogram.map(() => PALETTE[0])} />
          </Card>
          <Card title="Offered and achieved"
                subtitle={s.peak_in_flight != null && limit
                  ? `At most ${count(s.peak_in_flight)} of ${count(limit)} requests in flight`
                  : 'Users requested and agents answering'}
                help="Requested is the load the run offered. Answering is what the platform delivered. The gap between the two lines is the backlog; requests in flight is what the control-plane was still working on, which the run caps.">
            <TimeChart data={rows} labelKey="date" formatX={fmtX} phases={phases} legend
                       lines={[{ key: 'requested_total', label: 'Requested' },
                               { key: 'answering_total', label: 'Answering' },
                               { key: 'in_flight', label: 'In flight' }]}
                       colors={[STAGE_COLOR.requested, STAGE_COLOR.answering, STAGE_COLOR.created]}
                       height={offeredH} />
          </Card>
        </div>

        <Card title="Agent nodes and pending pods" span={6} subtitle="Nodes per pool and the warm buffer"
              help="Pending counts every pod in the cluster waiting for a node. Balloons are the warm buffer an agent pod preempts to start at once; when they run out, pods wait for Karpenter.">
          <TimeChart data={rows} labelKey="date" formatX={fmtX} phases={phases} legend
                     lines={[{ key: 'nodes_agent', label: 'Agent nodes' },
                             { key: 'nodes_general', label: 'General nodes' },
                             { key: 'cluster_pending', label: 'Pending pods' },
                             { key: 'balloons', label: 'Balloons' }]}
                     colors={[PALETTE[0], STAGE_COLOR.requested, STAGE_COLOR.waiting, PALETTE[10]]}
                     height={262} />
        </Card>
        <CapacityCard rows={rows} fmtX={fmtX} phases={phases} />
      </div>

      {(report.nodes || []).some(n => n.new) && (
        <div className="card card-flush">
          <div className="card-header">
            <h2 className="card-title">
              The nodes the run added
              <InfoTip label="The nodes the run added" width={320}>
                Karpenter adds nodes for the agents that found no room, then more to restore
                the warm buffer they took. Those hold none of the run's agents (0 below), and
                their image pull, read when phase 1 ends, is often not done by then.
              </InfoTip>
            </h2>
          </div>
          <div className="table-container">
            <table className="table table-stack">
              <thead><tr><th>Node</th><th>Type</th><th className="text-right">Agents</th><th>Created</th>
                <th>Launched</th><th>Registered</th><th>Initialised</th><th>Image pull</th><th>Gone</th></tr></thead>
              <tbody>
                {report.nodes.filter(n => n.new).map(n => (
                  <tr key={n.name}>
                    {/* The host part, one line a row; the full name on hover. */}
                    <td data-label="Node" className="mono nowrap" title={n.node || n.name}>
                      {String(n.node || n.name || '').split('.')[0]}
                    </td>
                    <td data-label="Type">{n.instance_type || '—'}</td>
                    <td data-label="Agents" className="text-right num">
                      {n.agents === null || n.agents === undefined ? '—' : count(n.agents)}
                    </td>
                    <td data-label="Created">{onRunDay(n.created)}</td>
                    <td data-label="Launched">{n.launched_s != null ? `+${n.launched_s} s` : '—'}</td>
                    <td data-label="Registered">{n.registered_s != null ? `+${n.registered_s} s` : '—'}</td>
                    <td data-label="Initialised">{n.initialized_s != null ? `+${n.initialized_s} s` : '—'}</td>
                    <td data-label="Image pull">{ms(n.pull_ms)}</td>
                    <td data-label="Gone">{onRunDay(n.gone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ProblemsTable failures={report.failures} problems={report.pod_problems} />
    </>
  );
}

// What went wrong with a pod (services/load_tests._problem_kind), worst first.
const PROBLEM_KIND = {
  oom_killed: ['danger', 'OOM killed'], pull_failed: ['danger', 'Pull failed'],
  failed: ['danger', 'Failed'], preempted: ['warn', 'Preempted'], evicted: ['warn', 'Evicted'],
  restarted: ['warn', 'Restarted'], pull_recovered: ['neutral', 'Pull recovered'],
};

/* The users that failed and the pods that had trouble, a row each. A pull that failed and
   then recovered cost time but is not a problem in the recap, which the header says when
   that is all there is. */
function ProblemsTable({ failures = [], problems = [] }) {
  if (!failures.length && !problems.length) return null;
  const recovered = problems.filter(p => p.kind === 'pull_recovered').length;
  const onlyRecovered = !failures.length && recovered === problems.length;
  const who = failures.length ? 'Pod or user' : 'Pod';
  return (
    <div className="card card-flush">
      <div className="card-header">
        <h2 className="card-title">{onlyRecovered ? 'Image pulls that recovered' : 'Pod problems'}</h2>
        <span className="text-sm text-muted">
          {onlyRecovered
            ? `${count(recovered)} ${recovered === 1 ? 'pod' : 'pods'} failed to pull the image at first, then started: not counted as a problem`
            : `${count(failures.length + problems.length)} in all${recovered ? `, ${count(recovered)} of them pulls that recovered` : ''}`}
        </span>
      </div>
      <div className="table-container">
        <table className="table table-stack">
          <thead><tr><th>{who}</th><th>What</th><th>Detail</th></tr></thead>
          <tbody>
            {failures.map(f => (
              <tr key={`u${f.seq}`}>
                <td data-label={who} className="mono break">{f.username}</td>
                <td data-label="What"><Badge tone="danger">User failed</Badge></td>
                <td data-label="Detail" className="wrap">{f.error || '—'}</td>
              </tr>
            ))}
            {problems.map(p => {
              const [tone, label] = PROBLEM_KIND[p.kind] || ['danger', String(p.kind || 'problem').replace('_', ' ')];
              const detail = [p.disruption, p.waiting_reason, p.restarts ? `${p.restarts} restarts` : '']
                .filter(Boolean).join(' · ');
              return (
                <tr key={p.pod}>
                  <td data-label={who} className="mono break">{p.pod}</td>
                  <td data-label="What"><Badge tone={tone}>{label}</Badge></td>
                  <td data-label="Detail" className="wrap">
                    {detail || '—'}
                    {p.failure?.log_tail && <pre className="mono text-xs mt-1 mb-0">{p.failure.log_tail}</pre>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* The Dashboard's LLM requests and Tools sections over a phase: the same cards, computed
   from the run's own events (`t.dashboard`, backend loadtest/dashboard_views.py), since a
   test user's calls never reach the tables the Dashboard reads. */
function PhaseDashboard({ t, real, timeline }) {
  const d = t.dashboard;
  const [source, setSource] = useState('all');
  const [latMetric, setLatMetric] = useState('total');
  const [tokMetric, setTokMetric] = useState('input');
  const [tool, setTool] = useState('');
  if (!d) return null;
  const fmtX = clockX(d.step_s || 60);
  const sources = d.sources || [];
  const views = d.llm || {};
  const llm = views[source === 'all' ? '' : source] || views[''] || {};
  const byTool = d.tool_latency?.by_tool || {};
  return (
    <>
      {real ? (
        <LlmRequestsSection
          latency={{ data: llm.llm_latency?.[latMetric] }}
          latencyMetric={latMetric} onLatencyMetric={setLatMetric}
          tokens={{ data: llm.tokens_per_request?.[tokMetric] }}
          tokensMetric={tokMetric} onTokensMetric={setTokMetric}
          cacheHitRate={llm.cache_hit_rate} truncated={llm.truncated}
          requestsPerTurn={llm.requests_per_turn}
          controls={sources.length > 1 ? (
            <Tabs size="sm" label="Source of the LLM requests" value={source} onChange={setSource}
                  tabs={[{ value: 'all', label: 'All sources' },
                         ...sources.map(s => ({ value: s, label: sourceName(s) }))]} />
          ) : null}
          formatX={fmtX} timeline={timeline} gapOnNull />
      ) : (
        <div className="notice notice-info" role="status">
          <div className="notice-body">
            This run answered with the mock model, so the Dashboard's LLM request cards are
            left out: their latency and tokens would be the mock's own settings.
          </div>
        </div>
      )}
      {t.n === 3 ? (
        <ToolsSection
          toolLatency={{ tools: d.tool_latency?.tools || [], data: byTool[tool] || byTool[''] }}
          tool={tool} onTool={setTool}
          turnLatency={d.turn_latency}
          topTools={d.top_tools} platformTools={d.platform_tools}
          formatX={fmtX} timeline={timeline} gapOnNull />
      ) : (
        <>
          <div className="section-head">
            <h2>Turns</h2>
            <p>a user's message to the last reply before their next one</p>
          </div>
          <div className="dashboard-grid">
            <TurnLatencyCard data={d.turn_latency?.data} formatX={fmtX} timeline={timeline} gapOnNull
                             span={12} />
          </div>
        </>
      )}
    </>
  );
}

function replyTimings(t) {
  return [{ rows: [
    { label: 'First reply after a message', row: t.round_trip, strong: true,
      help: 'From the worker handing a message to the bridge until the next reply reached that user\'s channel. When an agent falls behind, that reply answers an earlier message, so this is how long a user waited to hear anything.' },
    { label: 'Bridge to proxy-channels', row: t.accepted,
      help: 'From the worker handing the message to the bridge until proxy-channels recorded it: the bridge\'s forward path.' },
    { label: 'LLM call', row: t.llm?.latency,
      help: 'The agents\' own LLM calls, the ones a user waits on: the whole call.' },
    { label: 'LLM first token', row: t.llm?.ttft,
      help: 'The agents\' own LLM calls: from the request until the model\'s first token.' },
  ] }];
}

/* Phase 3's requests: per tool, how often it was asked for, answered, chosen, run and
   failed, in one table (the answers per tool used to sit in a second one, "What was
   asked", whose eleven rows left the proxy-crons card beside it mostly empty); then
   the scheduler's calls, beside the reminders and workflows asked for. */
function RequestsSection({ t }) {
  const tools = t.tools || [];
  const kinds = t.by_kind || [];
  // The requests that are not a tool, as the backend's `tools` leaves them out. A tool can
  // share the name (the agent's own `cron` tool): its row keeps the tool's figures only.
  const notTools = new Set(['cron', 'workflow', 'chat']);
  const answeredOf = Object.fromEntries(kinds.filter(k => !notTools.has(k.kind))
    .map(k => [k.kind, k.answered]));
  const toolRows = tools.map(r => ({ ...r, answered: answeredOf[r.tool] ?? 0 }));
  const toolNames = new Set(tools.map(r => r.tool));
  const others = kinds.filter(k => notTools.has(k.kind) || !toolNames.has(k.kind));
  return (
    <>
      <div className="section-head">
        <h2>Requests</h2>
        <p>what the run asked for, and what came of it</p>
      </div>
      <div className="dashboard-grid">
        <Card title="Tools asked for" span={12}
              help="Asked: messages that requested the tool; answered: those that had their reply. Chosen: the LLM called it (the proxy-tools catalogue tools, web search, web fetch and files, are chosen through call_mcp_tool, on that row). Invoked: proxy-tools ran it (native tools that never reach it show 0). With the outbound correction on (Config > Tools), proofread is invoked once per reply too.">
          <PctTable rows={toolRows} first="Tool" firstKey="tool"
                    extra={[{ key: 'asked', label: 'Asked' }, { key: 'answered', label: 'Answered' },
                            { key: 'chosen', label: 'Chosen by the LLM' },
                            { key: 'invoked', label: 'Invoked' }, { key: 'failed', label: 'Failed' }]} />
        </Card>
        <Card title="Scheduler (proxy-crons)" span={others.length ? 6 : 12}
              help="Every call the test agents made to proxy-crons: their cron state, reminders, workflow tasks and heartbeats.">
          <PctTable rows={t.crons} first="Call" firstKey="call"
                    extra={[{ key: 'calls', label: 'Calls' }, { key: 'failed', label: 'Failed' }]} />
        </Card>
        {others.length > 0 && (
          <Card title="Reminders and workflows" span={6}
                subtitle="Asked for, and answered"
                help="The requests of phase 3 that are not a tool: a reminder for a few minutes ahead, a workflow, or a plain message. Whether each reminder then fired is in the recap.">
            <PctTable rows={others} first="Request" firstKey="kind"
                      extra={[{ key: 'asked', label: 'Asked' }, { key: 'answered', label: 'Answered' }]} />
          </Card>
        )}
      </div>
    </>
  );
}

/* Phase 2 or 3 in the Dashboard tab. */
function MessagesSection({ n, report, timeline }) {
  const t = report.traffic?.[String(n)];
  const phase = (report.phase_list || []).find(p => p.n === n);
  const title = PHASES.find(p => p.n === n)?.label;
  if (!t) {
    return (
      <>
        <PhaseHead n={n} title={title} phase={phase} />
        <EmptyState icon="gauge" title="This phase has not started" />
      </>
    );
  }
  const real = report.run.params?.llm === 'real';
  const llm = t.llm || {};
  const finish = Object.entries(llm.finish_reasons || {});
  const errs = Object.entries(llm.error_kinds || {});
  const summary = `${count(t.answered)} of ${count(t.sent)} messages answered`
    + (t.waiting ? `, ${count(t.waiting)} waiting` : '');
  return (
    <>
      <PhaseHead n={n} title={title} phase={phase} summary={summary} />
      <PhaseDashboard t={t} real={real} timeline={timeline} />

      <div className="section-head">
        <h2>Replies</h2>
        <p>how long users waited, by channel, and how the LLM calls ended</p>
      </div>
      <div className="dashboard-grid">
        <Card title="Reply timings" span={6} subtitle="Each measured on its own">
          <TimingTable groups={replyTimings(t)} />
        </Card>
        <Card title="First reply after a message, distribution" span={6}
              empty={(t.round_trip_histogram || []).length ? null : 'No answer yet'}>
          <Histogram data={t.round_trip_histogram || []} unit="messages"
                     colors={(t.round_trip_histogram || []).map(() => PALETTE[1])} />
        </Card>
        <Card title="By channel" span={6} subtitle="Sent and answered, and the first reply after each">
          <PctTable rows={t.by_channel} first="Channel" firstKey="channel"
                    extra={[{ key: 'sent', label: 'Sent' }, { key: 'answered', label: 'Answered' }]} />
        </Card>
        <Card title="LLM finish reasons and errors" span={6}
              subtitle={`How the agents' own ${count(llm.agent_calls)} calls ended, and every error`}
              help="Finish reasons are the agents' own calls, the ones a user waits on. Errors are every call proxy-llm refused or that failed, whoever made it.">
          {finish.length || errs.length ? (
            <ul className="lt-reasons">
              {finish.map(([k, v]) => <li key={`f${k}`}><span className="mono">{k}</span><span className="num">{count(v)}</span></li>)}
              {errs.map(([k, v]) => (
                <li key={`e${k}`}><span><Badge tone="danger">error</Badge> <span className="mono">{k}</span></span><span className="num">{count(v)}</span></li>
              ))}
            </ul>
          ) : <p className="text-muted">No LLM call recorded.</p>}
        </Card>
        <Card title="LLM calls by source" span={12}
              subtitle={`${count(llm.calls)} calls, ${count(llm.tokens_out)} tokens out${llm.cost ? `, ${formatCost(llm.cost)} spent` : ''}`}
              help="As the Dashboard's Source filter names them. User: the agents' own calls, the ones a user waits on. Correction: the outbound correction of each reply, when it is on (Config > Tools): on a real tier as for a customer, never on the mock. External Tool: calls proxy-tools made for a tool. Voice: speech to text and text to speech.">
          <PctTable rows={(llm.by_source || []).map(r => ({ ...r, source: sourceName(r.source) }))}
                    first="Source" firstKey="source"
                    extra={[{ key: 'calls', label: 'Calls' }, { key: 'tokens_out', label: 'Tokens out' }]} />
        </Card>
      </div>

      {n === 3 && <RequestsSection t={t} />}
    </>
  );
}

function DashboardView({ report, timeline }) {
  const stopAfter = report.run.params?.stop_after || 1;
  return (
    <>
      <RunOverview report={report} timeline={timeline} />
      <OnboardingSection report={report} timeline={timeline} />
      {stopAfter >= 2 && <MessagesSection n={2} report={report} timeline={timeline} />}
      {stopAfter >= 3 && <MessagesSection n={3} report={report} timeline={timeline} />}
      {/* Keyed by the run, so another run's report starts on its first page. */}
      <UsersTable key={report.run.id} runId={report.run.id} />
    </>
  );
}

/* ── The Platform tab ── */

/* How a service fared over the run, judged as the Platform judges it now (its CPU and
   memory against the same thresholds, at their peak copy), with its server errors. */
function runHealth(view) {
  const m = view?.metrics || {};
  const peakOf = (block) => {
    const series = block?.series || {};
    const keys = Object.keys(series).filter(k => k !== 'service');
    const vals = (keys.length ? keys : ['service']).map(k => peakValue(series[k]))
      .filter(v => v !== null && v !== undefined);
    return vals.length ? Math.max(...vals) : null;
  };
  const total = block => (block?.series?.service || []).reduce((a, v) => a + (Number(v) || 0), 0);
  const cpu = peakOf(m.cpu);
  const mem = peakOf(m.memory);
  const errors = m.errors_5xx ? total(m.errors_5xx) : null;
  const requests = m.requests ? total(m.requests) : null;
  const reasons = [];
  let health = 'ok';
  const bump = (h, why) => {
    reasons.push(why);
    if (h === 'critical' || (h === 'warn' && health === 'ok')) health = h;
  };
  if (cpu >= THRESHOLDS.cpuCrit) bump('critical', `CPU peaked at ${Math.round(cpu)}%`);
  else if (cpu >= THRESHOLDS.cpuWarn) bump('warn', `CPU peaked at ${Math.round(cpu)}%`);
  if (mem >= THRESHOLDS.memCrit) bump('critical', `memory peaked at ${Math.round(mem)}%`);
  else if (mem >= THRESHOLDS.memWarn) bump('warn', `memory peaked at ${Math.round(mem)}%`);
  if (errors) bump('warn', `${count(errors)} server errors`);
  const replicas = view?.replicas || [];
  return {
    health, reasons, cpu, mem, errors, requests, copies: replicas.length,
    tasks: replicas.reduce((a, r) => a + ((r.tasks || []).length || 1), 0),
  };
}

const pct = v => (v === null || v === undefined ? '—' : `${Math.round(v)}%`);

/* The list on the left, as the Platform's Services view draws it: grouped by kind, a dot
   for how the service fared over the run, and how many copies ran. */
function RunServiceNav({ items, hasDb, selected, onSelect }) {
  return (
    <nav className="service-nav hide-sm" aria-label="Services">
      {['proxy', 'bridge', 'core'].map((kind) => {
        const list = items.filter(s => s.kind === kind);
        if (!list.length) return null;
        return (
          <div key={kind} className="service-nav-group">
            <div className="overline">{KIND_GROUP_LABEL[kind]}</div>
            {list.map(s => (
              <button key={s.id} type="button"
                      className={cx('service-nav-item', s.id === selected && 'is-active')}
                      aria-current={s.id === selected ? 'true' : undefined}
                      onClick={() => onSelect(s.id)}>
                <span className={`dot health-dot-${s.health}`} aria-hidden="true" />
                <span className="truncate">{s.id}</span>
                <span className="sr-only">, {HEALTH_LABEL[s.health]} during the run</span>
                {s.copies > 0 && (
                  <span className="service-nav-count num">
                    {s.copies}<span className="sr-only"> {s.copies === 1 ? 'copy' : 'copies'}</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        );
      })}
      {hasDb && (
        <div className="service-nav-group">
          <div className="overline">Data</div>
          <button type="button" className={cx('service-nav-item', selected === 'database' && 'is-active')}
                  aria-current={selected === 'database' ? 'true' : undefined}
                  onClick={() => onSelect('database')}>
            <Icon name="database" size={14} />
            <span className="truncate">Database</span>
          </button>
        </div>
      )}
    </nav>
  );
}

function RunService({ item, timeline }) {
  const { view } = item;
  return (
    <>
      <div className="card mb-4">
        <div className="row row-wrap gap-2">
          <h2 className="service-title">{item.id}</h2>
          <HealthBadge health={item.health} />
          <Badge tone="outline">{KIND_LABEL[item.kind] || item.kind}</Badge>
        </div>
        {item.role && <p className="text-muted mt-1 mb-0">{item.role}</p>}
        {item.reasons.length > 0 && (
          <div className={`notice ${item.health === 'critical' ? 'notice-danger' : 'notice-warn'} mt-3`}>
            <div className="notice-body">During the run: {item.reasons.join(' · ')}</div>
          </div>
        )}
        <dl className="kv mt-4">
          <dt>Copies</dt>
          <dd>{item.copies
            ? `${item.copies} ran during the run${item.tasks > item.copies ? `, ${item.tasks} tasks between them` : ''}`
            : 'not kept: the service as a whole'}</dd>
          <dt>Peak CPU</dt>
          <dd>{pct(item.cpu)} of the task reservation{item.copies > 1 ? ', at its busiest copy' : ''}</dd>
          <dt>Peak memory</dt>
          <dd>{pct(item.mem)} of the task reservation</dd>
          {item.requests !== null && (
            <><dt>Requests</dt><dd>{count(item.requests)} over the run's window</dd></>
          )}
          {item.errors !== null && (
            <><dt>Server errors</dt><dd>{count(item.errors)}</dd></>
          )}
        </dl>
      </div>
      <SourceWarnings warnings={view?.warnings} />
      {/* The copies and their charts, the Platform's own component. Keyed by the service,
          so each chart's copy view starts over on another service. */}
      <ServiceDetail key={item.id} data={view} formatX={platformX} timeline={timeline} past />
    </>
  );
}

/* The database over the run, as the Platform's Database view: its figures at their peak,
   then the same charts. */
function RunDatabase({ data, timeline }) {
  const a = data.aurora || {};
  const c = data.current || {};
  const sr = data.series || {};
  const peak = k => peakValue(sr[k]);
  const acu = peak('acu_writer');
  const conns = peak('connections');
  const connPct = c.max_connections && conns !== null ? (conns / c.max_connections) * 100 : null;
  const lag = peak('replica_lag_ms');
  return (
    <>
      <div className="card mb-4">
        <div className="row row-wrap gap-2">
          <h2 className="service-title">Database</h2>
          <Badge tone="outline">Aurora</Badge>
        </div>
        <p className="text-muted mt-1 mb-0">{data.proxy ? "The Aurora cluster and the RDS Proxy in front of it, over the run's window" : "The Aurora cluster over the run's window"}</p>
      </div>
      <div className="dashboard-grid">
        {a.serverless === false ? (
          <StatTile span={3} icon="database" label="Writer instance"
                    value={a.writer_class || '—'}
                    sub={peak('cpu_writer') !== null ? `fixed size · CPU peak ${Math.round(peak('cpu_writer'))}%` : 'fixed size'}
                    tone={peak('cpu_writer') >= 80 ? 'warning' : undefined} />
        ) : (
          <StatTile span={3} icon="database" label="Writer capacity, peak"
                    value={acu !== null ? `${acu} ACU` : '—'}
                    sub={`scales ${a.min_acu ?? '—'} to ${a.max_acu ?? '—'} ACU`}
                    tone={a.max_acu && acu >= a.max_acu * 0.9 ? 'warning' : undefined} />
        )}
        <StatTile span={3} icon="activity" label="Connections, peak"
                  value={conns !== null ? count(conns) : '—'}
                  sub={connPct === null ? '—' : `${Math.round(connPct)}% of max_connections`}
                  tone={connPct >= 80 ? 'negative' : connPct >= 60 ? 'warning' : undefined} />
        <StatTile span={3} icon="clock" label="Commit latency, peak"
                  value={peak('commit_latency_ms') !== null ? formatMs(peak('commit_latency_ms')) : '—'}
                  sub="writer, average per bucket" />
        <StatTile span={3} icon="layers" label="Replica lag, peak"
                  value={lag !== null ? formatMs(lag) : '—'} sub="reader behind writer"
                  tone={lag > 1000 ? 'warning' : undefined} />
      </div>
      <SourceWarnings warnings={data.warnings} />
      <DatabaseCharts data={data} formatX={platformX} timeline={timeline} past />
    </>
  );
}

function PlatformView({ report, timeline, waiting, service, onService }) {
  const g = report.general || {};
  const views = g.services_view;
  const catalogue = g.service_catalogue;
  const items = useMemo(() => Object.entries(views || {}).map(([id, view]) => ({
    id, view, kind: catalogue?.[id]?.kind || kindOf(id), role: catalogue?.[id]?.role || '',
    ...runHealth(view),
  })).sort((a, b) => (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9)
    || serviceRank(a.id) - serviceRank(b.id) || a.id.localeCompare(b.id)), [views, catalogue]);
  const hasDb = Boolean(g.database_view);
  const ids = [...items.map(s => s.id), ...(hasDb ? ['database'] : [])];
  const selected = ids.includes(service) ? service : ids[0];
  const note = viewsNote(report, waiting);
  const warnings = g.views_status?.warnings || [];
  return (
    <>
      {note && <div className="notice notice-info" role="status"><div className="notice-body">{note}</div></div>}
      {warnings.length > 0 && <p className="text-sm text-muted">Not read: {warnings.join(' · ')}</p>}
      {!ids.length ? (
        <EmptyState icon="infrastructure" title="No Platform view of this run yet" />
      ) : (
        <div className="platform-split">
          <RunServiceNav items={items} hasDb={hasDb} selected={selected} onSelect={onService} />
          <div className="min-w-0">
            <div className="show-sm mb-3">
              <label htmlFor="ltr-svc-pick" className="form-label">Service</label>
              <select id="ltr-svc-pick" className="form-input" value={selected}
                      onChange={e => onService(e.target.value)}>
                {ids.map(id => <option key={id} value={id}>{id === 'database' ? 'Database' : id}</option>)}
              </select>
            </div>
            {selected === 'database'
              ? <RunDatabase data={g.database_view} timeline={timeline} />
              : <RunService item={items.find(s => s.id === selected)} timeline={timeline} />}
          </div>
        </div>
      )}
    </>
  );
}

function RunReport({ runId, view, service, onView, onService, onBack }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(async () => {
    try {
      setReport(await readJson(await apiFetch(`/api/load-tests/${runId}/results`)));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [runId]);
  const live = report ? ACTIVE.includes(report.run.status) : true;
  // A finished run is read again until the Platform's views of it are in, for at most
  // VIEWS_WAIT_MS from its end or from opening the report, whichever is later.
  const opened = useRef(Date.now());
  const endedAt = report?.run?.ended_at ? Date.parse(report.run.ended_at) : 0;
  const waiting = viewsPending(report)
    && Date.now() - Math.max(opened.current, endedAt || 0) < VIEWS_WAIT_MS;
  usePolling(load, live ? 10000 : waiting ? 30000 : 0, [load]);
  useEffect(() => { load(); }, [load]);

  // The spans the charts draw, named for the strips and their tooltips.
  const timeline = useMemo(() => (report?.timeline || []).map(s => ({
    ...s,
    label: s.kind === 'phase' ? (PHASES.find(p => p.n === s.n)?.label || s.name)
      : s.kind === 'hold' ? 'Hold' : 'Cleanup',
    ...(s.kind === 'phase' ? PHASE_PARTS[s.n] : {}),
  })), [report]);

  if (!report && !error) return <TableSkeleton rows={6} cols={4} label="Loading the report" />;
  if (!report) return <EmptyState icon="alertCircle" title="The report did not load">{error}</EmptyState>;

  const { run } = report;
  const stop = async () => {
    if (!await confirm({ title: 'Stop this run?', body: 'No more users or messages are started, and the cleanup begins.',
                         confirmLabel: 'Stop the run', tone: 'danger' })) return;
    await callApi(`/api/load-tests/${run.id}/stop`, { method: 'POST' },
                  { successMsg: 'Stop requested', errorMsg: 'Stop failed' });
    load();
  };
  const tab = view === 'platform' ? 'platform' : 'dashboard';

  return (
    <>
      <RunHeader report={report} onBack={onBack} onStop={stop} />
      <RunKpis report={report} />
      {run.started_at && timeline.length > 0 && <RunTimeline run={run} timeline={timeline} />}

      <div className="dashboard-tabs">
        <Tabs variant="line" label="Report views" idPrefix="ltr" value={tab} onChange={onView}
              tabs={REPORT_TABS} />
      </div>
      <div role="tabpanel" id={`ltr-panel-${tab}`} aria-labelledby={`ltr-tab-${tab}`}>
        {tab === 'dashboard'
          ? <DashboardView report={report} timeline={timeline} />
          : <PlatformView report={report} timeline={timeline} waiting={waiting}
                          service={service} onService={onService} />}
      </div>
      {confirmDialog}
    </>
  );
}

/* ── The page ───────────────────────────────────────────────────────────── */

function LoadTests() {
  const [params, setParams] = useSearchParams();
  const runId = params.get('run');
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState('new');
  // The run whose settings Run again put in New run, and when: each press starts the form
  // afresh, from that run's settings.
  const [rerun, setRerun] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await readJson(await apiFetch('/api/load-tests/status')));
    } catch (_) {
      setStatus({ enabled: false, active_run: null, test_types: [] });
    }
  }, []);
  usePolling(loadStatus, 15000, [loadStatus]);

  const open = (id) => setParams({ run: id });
  const back = () => { setParams({}); setTab('runs'); };
  const runAgain = (run) => { setRerun({ run, at: Date.now() }); setTab('new'); };
  // Pressed far down the list, Run again would leave the top of the form out of view: the
  // tabs back in view, and the focus on New run's tab, where choosing that tab leaves it.
  useEffect(() => {
    if (!rerun) return;
    const newTab = document.getElementById('lt-tab-new');
    newTab?.scrollIntoView({ block: 'nearest' });
    newTab?.focus({ preventScroll: true });
  }, [rerun]);
  // The report's tab and service ride in the query string, as the Platform page's, so a
  // link opens the same view: ?run=<id>&view=platform&service=proxy-llm.
  const setReportParam = useCallback((key, value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'view' && value !== 'platform') next.delete('service');
    return next;
  }, { replace: true }), [setParams]);

  return (
    <div className="page">
      <PageHeader description="Load the platform with test users and see how it holds up: how long each user waits for an agent that answers, how its messages and tool calls fare, and what every service does meanwhile.">
        <h1 className="page-title">Load tests</h1>
      </PageHeader>

      {status && !status.enabled && (
        <div className="notice notice-warn" role="status">
          Load tests are not enabled on this environment (LOAD_TESTS_ENABLED). The page is read-only.
        </div>
      )}
      {status?.active_run && !runId && (
        <div className="notice notice-info" role="status">
          A run is active: <strong>{status.active_run.name}</strong> ({status.active_run.status}).
          A new run can start once it ends.{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => open(status.active_run.id)}>
            Open it
          </button>
        </div>
      )}

      {runId ? (
        <RunReport runId={runId} onBack={back}
                   view={params.get('view')} onView={v => setReportParam('view', v === 'dashboard' ? '' : v)}
                   service={params.get('service')} onService={id => setReportParam('service', id)} />
      ) : (
        <>
          {/* The page's sections, as every page draws them (Broadcast, Events): line tabs
              with icons, the same look as the report's Dashboard and Platform tabs. */}
          <div className="page-tabs">
            <Tabs variant="line" label="Load tests" value={tab} onChange={setTab} idPrefix="lt"
                  tabs={PAGE_TABS} />
          </div>
          <div role="tabpanel" id={`lt-panel-${tab}`} aria-labelledby={`lt-tab-${tab}`}>
            {tab === 'new'
              ? <NewRun key={rerun ? rerun.at : 'defaults'} status={status} from={rerun?.run || null}
                        onClear={() => setRerun(null)}
                        onStarted={(id) => { setRerun(null); open(id); }} />
              : <RunsList onOpen={open} onRerun={runAgain} />}
          </div>
        </>
      )}
    </div>
  );
}

export default LoadTests;
